import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/services/api';
import { announcementsCache } from '@/services/announcementsCache';

export default function InviteRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pre-fill code if passed via navigation state
  useEffect(() => {
    if (location.state?.code) {
      setCode(location.state.code);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (code.length !== 6) {
      setError('Code must be 6 digits');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const res = await api.post('/invites/register', {
        code,
        username,
        email: email || null,
        password,
      });

      // Store tokens
      localStorage.setItem('accessToken', res.data.accessToken);
      localStorage.setItem('refreshToken', res.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      window.dispatchEvent(new Event('user-updated'));
      announcementsCache.clear(); // Clear dismissed announcements cache on fresh registration

      setSuccess('Account created successfully! Redirecting...');
      setTimeout(() => navigate('/chat?dashboard=1'), 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-background flex items-center justify-center p-4">
      <div className="max-w-md w-full glass-effect rounded-2xl shadow-2xl border border-brand-primary/30 p-8">
        <div className="flex justify-center mb-6">
          <div className="w-24 h-24 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-full flex items-center justify-center text-4xl">
            🎫
          </div>
        </div>
        <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-secondary mb-2 text-center">
          Create Account
        </h1>
        <p className="text-brand-on-background/60 mb-6 text-center">
          Enter your invite code to get started.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 text-green-200 rounded-lg">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-on-background mb-2">
              6-Digit Invite Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full px-4 py-3 glass-effect border border-brand-primary/30 rounded-lg text-brand-on-background focus:outline-none focus:border-brand-primary text-center text-2xl tracking-widest font-mono"
              required
              maxLength={6}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-on-background mb-2">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Choose a username"
              className="w-full px-4 py-3 glass-effect border border-brand-primary/30 rounded-lg text-brand-on-background focus:outline-none focus:border-brand-primary"
              required
              minLength={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-on-background mb-2">
              Email (Optional)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              className="w-full px-4 py-3 glass-effect border border-brand-primary/30 rounded-lg text-brand-on-background focus:outline-none focus:border-brand-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-on-background mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="w-full px-4 py-3 glass-effect border border-brand-primary/30 rounded-lg text-brand-on-background focus:outline-none focus:border-brand-primary"
              required
              minLength={8}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-on-background mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              className="w-full px-4 py-3 glass-effect border border-brand-primary/30 rounded-lg text-brand-on-background focus:outline-none focus:border-brand-primary"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full py-3 bg-brand-primary hover:bg-brand-primary-container text-brand-on-background rounded-lg font-semibold disabled:opacity-50 transition-all duration-300"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="text-center text-sm text-brand-on-background/60">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-brand-on-background hover:text-brand-on-background transition-colors"
            >
              Login here
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
